import { Stack } from '@mui/material'
import { useContext } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { FlexBox, GapBox } from 'components/Box'
import { BoxForm, NumberField, Switch, SwitchWithAutoButton } from 'components/Form'
import { SectionHeader } from 'components/Header'
import Paper from 'components/Paper'
import { Alert } from 'features/alerts/types/alert'
import { AdUnitViewabilityToolsAlert } from 'features/globalSetup/api/types/adUnitViewabilityToolsAlert'
import { AddEditAdUnitContext } from 'features/inventory/ad-unit/contexts/AddEditAdUnitContext'
import { useAbility } from 'providers/casl'
import { CONTAINER_SPACING } from 'providers/material-ui/theme/constants'
import { AdUnitAlerts } from '../../components/AdUnitAlerts'
import { AdUnitAlert } from '../../types/adUnitAlert'
import { DomainRelatedSettings } from './DomainRelatedSettings'
import { Schema } from './schema'
import { useTooltips } from './useTooltips'
import { ViewabilityToolsAlerts } from './ViewabilityToolsAlerts'

export const Form = (): JSX.Element => {
  const { t } = useTranslation('features/adUnit', { keyPrefix: 'form.viewabilityTools' })
  const { adUnit } = useContext(AddEditAdUnitContext)
  const ability = useAbility()
  const cannotUpdateAdUnit = ability.cannot('update', 'AdUnit')

  if (!adUnit) {
    throw Error('Ad unit should be accessible')
  }

  const {
    adUnitViewabilityTool: { alerts: viewabilityToolAlerts },
    alerts: adUnitAlerts,
    domain: {
      domainViewabilityTools: {
        gamAdRequestMargin: domainGamAdRequestMargin,
        kvTargetingAndPrebidAuction: domainKvTargetingAndPrebidAuction,
        lazyLoadingEnabled: domainLazyLoadingEnabled,
        lazyLoadTargetingPrebidAuctionEnabled: domainLazyLoadTargetingPrebidAuctionEnabled,
        lazyLoadType: domainLazyLoadType,
        mobileScaling: domainMobileScaling,
      },
      id: domainId,
    },
  } = adUnit

  const { adHoldingTooltip, lazyLoadingTooltip, scrollSlowingTooltip } = useTooltips()

  const { control, watch } = useFormContext<Schema>()
  const {
    adHoldingAutoEnabled,
    customizeLazyLoadingParametersEnabled,
    lazyLoadingAutoEnabled,
    lazyLoadingEnabled,
    lazyLoadTargetingPrebidAuctionEnabled,
    scrollSlowingAutoEnabled,
  } = watch()

  const lazyLoadingHint =
    domainLazyLoadingEnabled &&
    domainLazyLoadType === 'googlePublisherTagsLazyLoad' &&
    lazyLoadingEnabled
      ? t('basicSettings.lazyLoading.hint')
      : ''

  const switchHint = (value: boolean): string => (value ? t('basicSettings.switchHint') : '')

  const viewabilityToolsProductAlerts: Alert<AdUnitAlert>[] = adUnitAlerts.filter(
    (alert: Alert<AdUnitAlert>): boolean => alert.name.startsWith('viewabilityTools'),
  )

  return (
    <>
      <Paper>
        <SectionHeader
          subtitle={t('enabled.description')}
          title={t('enabled.title')}
          titleEndAdornment={
            <FlexBox axis='x'>
              <Switch
                control={control}
                disabled={cannotUpdateAdUnit}
                name='viewabilityToolsEnabled'
              />

              {viewabilityToolsProductAlerts.length > 0 && (
                <AdUnitAlerts alerts={viewabilityToolsProductAlerts} />
              )}
            </FlexBox>
          }
        />
      </Paper>

      <Paper>
        <SectionHeader
          subtitle={t('basicSettings.subtitle')}
          title={t('basicSettings.title')}
        />

        <GapBox />

        <BoxForm>
          <FlexBox
            axis='x'
            gap={1}
          >
            <SwitchWithAutoButton
              control={control}
              disabled={cannotUpdateAdUnit}
              label={t('basicSettings.adHolding.label')}
              name='adHolding'
              switchButtonHint={switchHint(adHoldingAutoEnabled)}
              tooltip={adHoldingTooltip}
            />

            {viewabilityToolAlerts.length > 0 && (
              <ViewabilityToolsAlerts
                alerts={viewabilityToolAlerts.filter(
                  (alert: Alert<AdUnitViewabilityToolsAlert>): boolean =>
                    alert.name.startsWith('adHolding'),
                )}
              />
            )}
          </FlexBox>

          <FlexBox
            axis='x'
            gap={1}
          >
            <SwitchWithAutoButton
              control={control}
              disabled={cannotUpdateAdUnit}
              label={t('basicSettings.scrollSlowing.label')}
              name='scrollSlowing'
              switchButtonHint={switchHint(scrollSlowingAutoEnabled)}
              tooltip={scrollSlowingTooltip}
            />

            {viewabilityToolAlerts.length > 0 && (
              <ViewabilityToolsAlerts
                alerts={viewabilityToolAlerts.filter(
                  (alert: Alert<AdUnitViewabilityToolsAlert>): boolean =>
                    alert.name.startsWith('scrollSlowing'),
                )}
              />
            )}
          </FlexBox>

          <FlexBox
            axis='x'
            gap={1}
          >
            <SwitchWithAutoButton
              autoButtonHint={lazyLoadingHint}
              control={control}
              disabled={
                cannotUpdateAdUnit ||
                (domainLazyLoadingEnabled && domainLazyLoadType === 'googlePublisherTagsLazyLoad')
              }
              label={t('basicSettings.lazyLoading.label')}
              name='lazyLoading'
              switchButtonHint={switchHint(lazyLoadingAutoEnabled)}
              tooltip={lazyLoadingTooltip}
            />

            {viewabilityToolAlerts.length > 0 && (
              <ViewabilityToolsAlerts
                alerts={viewabilityToolAlerts.filter(
                  (alert: Alert<AdUnitViewabilityToolsAlert>): boolean =>
                    alert.name.startsWith('lazyLoading'),
                )}
              />
            )}
          </FlexBox>

          {(lazyLoadingEnabled || lazyLoadingAutoEnabled) &&
            domainLazyLoadType === 'yieldbirdLazyLoad' && (
              <Stack
                alignItems='flex-start'
                direction='row'
                ml={CONTAINER_SPACING}
                spacing={2}
              >
                <BoxForm>
                  <Switch
                    control={control}
                    disabled={cannotUpdateAdUnit}
                    label={t('basicSettings.customizeLazyLoadingParametersEnabled.label')}
                    labelPlacement='start'
                    name='customizeLazyLoadingParametersEnabled'
                    tooltip={{
                      content: t('basicSettings.customizeLazyLoadingParametersEnabled.tooltip'),
                    }}
                  />

                  {customizeLazyLoadingParametersEnabled && (
                    <>
                      <Switch
                        control={control}
                        disabled={cannotUpdateAdUnit}
                        label={t('basicSettings.lazyLoadTargetingPrebidAuctionEnabled.label')}
                        labelPlacement='start'
                        name='lazyLoadTargetingPrebidAuctionEnabled'
                        tooltip={{
                          content: t('basicSettings.lazyLoadTargetingPrebidAuctionEnabled.tooltip'),
                        }}
                      />

                      {lazyLoadTargetingPrebidAuctionEnabled && (
                        <NumberField
                          control={control}
                          disabled={cannotUpdateAdUnit}
                          label={t('basicSettings.kvTargetingAndPrebidAuction.label')}
                          name='kvTargetingAndPrebidAuction'
                          sx={{ ml: CONTAINER_SPACING }}
                          tooltip={{
                            content: t('basicSettings.kvTargetingAndPrebidAuction.tooltip'),
                          }}
                          width='md'
                        />
                      )}

                      <NumberField
                        control={control}
                        disabled={cannotUpdateAdUnit}
                        label={t('basicSettings.gamAdRequestMargin.label')}
                        name='gamAdRequestMargin'
                        tooltip={{
                          content: t('basicSettings.gamAdRequestMargin.tooltip'),
                        }}
                        width='md'
                      />

                      <NumberField
                        control={control}
                        disabled={cannotUpdateAdUnit}
                        label={t('basicSettings.mobileScaling.label')}
                        name='mobileScaling'
                        tooltip={{
                          content: t('basicSettings.mobileScaling.tooltip'),
                        }}
                        width='md'
                      />
                    </>
                  )}
                </BoxForm>

                {customizeLazyLoadingParametersEnabled && (
                  <DomainRelatedSettings
                    gamAdRequestMargin={domainGamAdRequestMargin}
                    id={domainId}
                    kvTargetingAndPrebidAuction={domainKvTargetingAndPrebidAuction}
                    lazyLoadTargetingPrebidAuctionEnabled={
                      domainLazyLoadTargetingPrebidAuctionEnabled
                    }
                    mobileScaling={domainMobileScaling}
                  />
                )}
              </Stack>
            )}
        </BoxForm>
      </Paper>
    </>
  )
}
